// LeadIntelligenceContext — one fetch, many consumers.
//
// The Morning Brief banner AND every LeadCard render need the same
// per-lead intelligence pack from /api/leads/morning-brief. If
// each fetched separately, we'd hammer the endpoint N+1 times per
// pageview. The Provider fetches once, exposes the response via
// context, and any child reads via useLeadIntelligence().
//
// Provider takes { leads, callsByLeadId, outreachByLeadId } and
// refetches whenever leads change. Debounces so a rapid setLeads
// during a bulk import doesn't spam the endpoint.

import React from 'react';
import { readPersistedJwtDirect } from './authedFetch';
import { supabase } from '../lib/supabase';

const Ctx = React.createContext({ loading: false, data: null, error: null });

async function bearer() {
  const token = readPersistedJwtDirect();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function LeadIntelligenceProvider({ leads, callsByLeadId, outreachByLeadId, children }) {
  const [state, setState] = React.useState({ loading: true, data: null, error: null });

  // Debounce refetch — leads array identity changes on every render
  // if callers don't memoize. We reset the debounce whenever the
  // *content* signature changes.
  const signature = React.useMemo(() => {
    return (leads || []).map(l => `${l.id}:${l.status}:${l.updatedAt || l.createdAt || ''}`).join('|');
  }, [leads]);

  React.useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await fetch('/api/leads/morning-brief', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await bearer()) },
          body: JSON.stringify({
            leads: (leads || []).slice(0, 500),
            callHistoryByLeadId: callsByLeadId || {},
            outreachByLeadId:    outreachByLeadId || {},
          }),
        });
        const j = await r.json();
        if (cancelled) return;
        if (r.ok && j.ok) setState({ loading: false, data: j, error: null });
        else setState({ loading: false, data: null, error: j.error || `HTTP ${r.status}` });
      } catch (err) {
        if (!cancelled) setState({ loading: false, data: null, error: err.message });
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [signature, callsByLeadId, outreachByLeadId, leads]);

  return <Ctx.Provider value={state}>{children}</Ctx.Provider>;
}

export function useLeadIntelligence() {
  return React.useContext(Ctx);
}

// Convenience: read one lead's insight pack (or null if not scored
// or still loading).
export function useLeadInsight(leadId) {
  const { data } = useLeadIntelligence();
  return data?.insights_by_lead_id?.[leadId] || null;
}
